/**
 * Auth 状態の Composition 入口（F2 facade）。
 * 正本 client は firebase-lib。ここでは modular onAuthStateChanged で module 級 ref を同期する。
 * Pinia store 化はしない（火曜 merge / 本ブランチの対象外）。
 */
import { computed, shallowRef, type ComputedRef, type ShallowRef } from 'vue'
import {
  getFirebaseAuth,
  onAuthStateChanged,
  type User
} from '@/infra/firebase/auth'
import { getFirebaseClient } from '@/plugins/firebase-lib'

export type AuthUser = User

const user: ShallowRef<AuthUser | null> = shallowRef(null)

let authReadyResolve: ((u: AuthUser | null) => void) | null = null
let authReadySettled = false
const authReady: Promise<AuthUser | null> = new Promise((resolve) => {
  authReadyResolve = resolve
})

let bridgeInstalled = false
let attachAttempts = 0
const MAX_ATTACH_ATTEMPTS = 120

function settleAuthReady(next: AuthUser | null) {
  if (authReadySettled) return
  authReadySettled = true
  authReadyResolve?.(next)
  authReadyResolve = null
}

function syncUser(next: AuthUser | null, settle = false) {
  user.value = next
  if (settle) settleAuthReady(next)
}

function attachAuthBridge() {
  const client = getFirebaseClient()
  if (!client) {
    attachAttempts += 1
    if (attachAttempts < MAX_ATTACH_ATTEMPTS) {
      setTimeout(attachAuthBridge, 50)
    }
    return
  }

  user.value = (client.user as AuthUser | null) ?? null

  onAuthStateChanged(getFirebaseAuth(), (next) => {
    syncUser(next, true)
  })

  const ready = client.authReady as Promise<AuthUser | null> | undefined
  if (ready && typeof ready.then === 'function') {
    ready.then((next) => {
      if (next !== undefined) {
        user.value = next
      }
    })
  }
}

function ensureBridge() {
  if (bridgeInstalled) return
  bridgeInstalled = true
  attachAuthBridge()
}

export type UseAuthReturn = {
  user: ShallowRef<AuthUser | null>
  isAuthenticated: ComputedRef<boolean>
  authReady: Promise<AuthUser | null>
  whenAuthReady: () => Promise<AuthUser | null>
  signOut: (options?: { skipMessage?: boolean }) => void | undefined
  /** F2 未完了時の逃げ道。新規ロジックでは増やさない */
  getClient: typeof getFirebaseClient
}

/**
 * Auth 状態・手続き。どこから呼んでも同じ module 級状態を返す。
 */
export function useAuth(): UseAuthReturn {
  ensureBridge()

  const isAuthenticated = computed(() => !!(user.value && user.value.uid))

  async function whenAuthReady() {
    ensureBridge()
    const client = getFirebaseClient()
    if (client?.authReady) {
      await client.authReady
    } else {
      await authReady
    }
    const latest = (getFirebaseClient()?.user as AuthUser | null) ?? user.value
    user.value = latest
    return latest
  }

  function signOut(options?: { skipMessage?: boolean }) {
    return getFirebaseClient()?.signOut(options)
  }

  return {
    user,
    isAuthenticated,
    authReady,
    whenAuthReady,
    signOut,
    getClient: getFirebaseClient
  }
}
